import {
  VenueAdmissionType,
  VenueLifecycleStatus,
  VenueType,
} from '../enum/EnumRepository';
import {
  VenueConflictData,
  VenueConflictOpeningHour,
  VenueFieldDiff,
  VenueOpeningHoursData,
} from '../dto/EntityRepository';
import {
  CreateVenueReq,
  ResolveVenueConflictsReq,
  UpdateVenueReq,
} from '../dto/ReqDtoRepository';

export const VENUE_CONFLICT_FIELDS = [
  'name',
  'nameEn',
  'type',
  'countryCode',
  'city',
  'district',
  'roadAddress',
  'roadAddressEn',
  'latitude',
  'longitude',
  'timeZone',
  'instagramUrl',
  'websiteUrl',
  'phoneNumber',
  'description',
  'descriptionEn',
  'admissionType',
  'admissionNote',
  'openingHours',
  'openingHoursNote',
  'naverMapUrl',
  'googleMapUrl',
  'venueTag',
] as const;

export type VenueConflictField = typeof VENUE_CONFLICT_FIELDS[number];
export type VenueConflictChoice = 'EXISTING' | 'INCOMING';
export type VenueConflictChoices = Partial<
  Record<VenueConflictField, VenueConflictChoice>
>;

const STRING_FIELDS = [
  'name',
  'nameEn',
  'countryCode',
  'city',
  'district',
  'roadAddress',
  'roadAddressEn',
  'timeZone',
  'instagramUrl',
  'websiteUrl',
  'phoneNumber',
  'description',
  'descriptionEn',
  'admissionNote',
  'openingHoursNote',
  'naverMapUrl',
  'googleMapUrl',
] as const;

type VenueStringField = typeof STRING_FIELDS[number];

export const getVenueConflictFields = (
  conflict: VenueConflictData,
): VenueConflictField[] =>
  VENUE_CONFLICT_FIELDS.filter((field) => conflict[field] != null);

const assertResolved = (
  conflict: VenueConflictData,
  choices: VenueConflictChoices,
) => {
  const unresolved = getVenueConflictFields(conflict)
    .filter((field) => choices[field] !== 'EXISTING' && choices[field] !== 'INCOMING');
  if (unresolved.length > 0) {
    throw new Error(`Unresolved venue conflict fields: ${unresolved.join(', ')}`);
  }
};

const text = (value: string | null | undefined) => value ?? '';

const pick = <T>(
  conflict: VenueConflictData,
  field: VenueConflictField,
  choices: VenueConflictChoices,
  fallback: T,
): T => {
  const diff = conflict[field] as VenueFieldDiff<T> | undefined;
  if (!diff) return fallback;
  return choices[field] === 'EXISTING' ? diff.existing : diff.incoming;
};

const pickString = (
  conflict: VenueConflictData,
  field: VenueStringField,
  choices: VenueConflictChoices,
  fallback: string | undefined,
) => text(pick(conflict, field, choices, text(fallback)));

const toOpeningHours = (
  hours: VenueConflictOpeningHour[] | null | undefined,
): VenueOpeningHoursData[] => (hours ?? []).map((hour) => (
  hour.closed
    ? {
      dayOfWeek: hour.dayOfWeek,
      closed: true as const,
      opensAt: null,
      closesAt: null,
    }
    : {
      dayOfWeek: hour.dayOfWeek,
      closed: false as const,
      opensAt: hour.opensAt ?? '',
      closesAt: hour.closesAt ?? '',
    }
));

const pickOpeningHours = (
  incoming: CreateVenueReq,
  conflict: VenueConflictData,
  choices: VenueConflictChoices,
): VenueOpeningHoursData[] => {
  if (!conflict.openingHours) return incoming.openingHours ?? [];
  const selected = choices.openingHours === 'EXISTING'
    ? conflict.openingHours.existing
    : conflict.openingHours.incoming;
  return toOpeningHours(selected);
};

const pickTagIds = (
  conflict: VenueConflictData,
  choices: VenueConflictChoices,
  tagIdByName: ReadonlyMap<string, number>,
): number[] | undefined => {
  if (!conflict.venueTag || choices.venueTag === 'EXISTING') return undefined;
  const name = conflict.venueTag.incoming;
  if (!name) return [];
  const tagId = tagIdByName.get(name);
  if (tagId == null) {
    throw new Error(`Unknown venue tag: ${name}`);
  }
  return [tagId];
};

export const buildResolvedVenue = (
  incoming: CreateVenueReq,
  conflict: VenueConflictData,
  choices: VenueConflictChoices,
  lifecycleStatus: VenueLifecycleStatus,
  tagIdByName: ReadonlyMap<string, number>,
): UpdateVenueReq => {
  assertResolved(conflict, choices);
  const latitude = pick(conflict, 'latitude', choices, incoming.latitude ?? null);
  const longitude = pick(conflict, 'longitude', choices, incoming.longitude ?? null);
  const tagIds = pickTagIds(conflict, choices, tagIdByName);
  return {
    name: pickString(conflict, 'name', choices, incoming.name),
    type: pick(conflict, 'type', choices, incoming.type),
    countryCode: pickString(conflict, 'countryCode', choices, incoming.countryCode),
    city: pickString(conflict, 'city', choices, incoming.city),
    roadAddress: pickString(conflict, 'roadAddress', choices, incoming.roadAddress),
    timeZone: pickString(conflict, 'timeZone', choices, incoming.timeZone),
    lifecycleStatus,
    admissionType: pick(
      conflict,
      'admissionType',
      choices,
      incoming.admissionType ?? VenueAdmissionType.UNKNOWN,
    ),
    openingHours: pickOpeningHours(incoming, conflict, choices),
    nameEn: pickString(conflict, 'nameEn', choices, incoming.nameEn),
    district: pickString(conflict, 'district', choices, incoming.district),
    roadAddressEn: pickString(conflict, 'roadAddressEn', choices, incoming.roadAddressEn),
    latitude,
    longitude,
    instagramUrl: pickString(conflict, 'instagramUrl', choices, incoming.instagramUrl),
    websiteUrl: pickString(conflict, 'websiteUrl', choices, incoming.websiteUrl),
    phoneNumber: pickString(conflict, 'phoneNumber', choices, incoming.phoneNumber),
    description: pickString(conflict, 'description', choices, incoming.description),
    descriptionEn: pickString(conflict, 'descriptionEn', choices, incoming.descriptionEn),
    naverMapUrl: pickString(conflict, 'naverMapUrl', choices, incoming.naverMapUrl),
    googleMapUrl: pickString(conflict, 'googleMapUrl', choices, incoming.googleMapUrl),
    admissionNote: pickString(conflict, 'admissionNote', choices, incoming.admissionNote),
    openingHoursNote: pickString(
      conflict,
      'openingHoursNote',
      choices,
      incoming.openingHoursNote,
    ),
    ...(tagIds !== undefined ? { tagIds } : {}),
  };
};

const optionalText = (value: string) => value || undefined;

export const buildResolvedVenueCreate = (
  incoming: CreateVenueReq,
  conflict: VenueConflictData,
  choices: VenueConflictChoices,
): CreateVenueReq => {
  assertResolved(conflict, choices);
  const latitude = pick(conflict, 'latitude', choices, incoming.latitude ?? null);
  const longitude = pick(conflict, 'longitude', choices, incoming.longitude ?? null);
  const openingHours = pickOpeningHours(incoming, conflict, choices);
  const admissionType = pick(
    conflict,
    'admissionType',
    choices,
    incoming.admissionType ?? VenueAdmissionType.UNKNOWN,
  );
  const venueTag = conflict.venueTag
    ? (choices.venueTag === 'EXISTING'
      ? conflict.venueTag.existing
      : conflict.venueTag.incoming)
    : incoming.venueTag;

  return {
    venueCode: incoming.venueCode,
    name: pickString(conflict, 'name', choices, incoming.name),
    type: pick(conflict, 'type', choices, incoming.type) as VenueType,
    countryCode: pickString(conflict, 'countryCode', choices, incoming.countryCode),
    city: pickString(conflict, 'city', choices, incoming.city),
    roadAddress: pickString(conflict, 'roadAddress', choices, incoming.roadAddress),
    timeZone: pickString(conflict, 'timeZone', choices, incoming.timeZone),
    ...(optionalText(pickString(conflict, 'nameEn', choices, incoming.nameEn))
      ? { nameEn: pickString(conflict, 'nameEn', choices, incoming.nameEn) }
      : {}),
    ...(optionalText(pickString(conflict, 'district', choices, incoming.district))
      ? { district: pickString(conflict, 'district', choices, incoming.district) }
      : {}),
    ...(optionalText(pickString(conflict, 'roadAddressEn', choices, incoming.roadAddressEn))
      ? { roadAddressEn: pickString(conflict, 'roadAddressEn', choices, incoming.roadAddressEn) }
      : {}),
    ...(latitude != null || longitude != null ? { latitude, longitude } : {}),
    ...(optionalText(pickString(conflict, 'instagramUrl', choices, incoming.instagramUrl))
      ? { instagramUrl: pickString(conflict, 'instagramUrl', choices, incoming.instagramUrl) }
      : {}),
    ...(optionalText(pickString(conflict, 'websiteUrl', choices, incoming.websiteUrl))
      ? { websiteUrl: pickString(conflict, 'websiteUrl', choices, incoming.websiteUrl) }
      : {}),
    ...(optionalText(pickString(conflict, 'phoneNumber', choices, incoming.phoneNumber))
      ? { phoneNumber: pickString(conflict, 'phoneNumber', choices, incoming.phoneNumber) }
      : {}),
    ...(optionalText(pickString(conflict, 'description', choices, incoming.description))
      ? { description: pickString(conflict, 'description', choices, incoming.description) }
      : {}),
    ...(optionalText(pickString(conflict, 'descriptionEn', choices, incoming.descriptionEn))
      ? { descriptionEn: pickString(conflict, 'descriptionEn', choices, incoming.descriptionEn) }
      : {}),
    ...(admissionType ? { admissionType } : {}),
    ...(optionalText(pickString(conflict, 'admissionNote', choices, incoming.admissionNote))
      ? { admissionNote: pickString(conflict, 'admissionNote', choices, incoming.admissionNote) }
      : {}),
    ...(openingHours.length > 0 ? { openingHours } : {}),
    ...(optionalText(pickString(conflict, 'openingHoursNote', choices, incoming.openingHoursNote))
      ? {
        openingHoursNote: pickString(
          conflict,
          'openingHoursNote',
          choices,
          incoming.openingHoursNote,
        ),
      }
      : {}),
    ...(optionalText(pickString(conflict, 'naverMapUrl', choices, incoming.naverMapUrl))
      ? { naverMapUrl: pickString(conflict, 'naverMapUrl', choices, incoming.naverMapUrl) }
      : {}),
    ...(optionalText(pickString(conflict, 'googleMapUrl', choices, incoming.googleMapUrl))
      ? { googleMapUrl: pickString(conflict, 'googleMapUrl', choices, incoming.googleMapUrl) }
      : {}),
    ...(venueTag ? { venueTag } : {}),
  };
};

export interface VenueConflictApplyItem {
  rowNumber: number;
  venueId: number | null;
  venueCode: string;
  incoming: CreateVenueReq;
  conflict: VenueConflictData;
  choices: VenueConflictChoices;
}

/** 저장된 Venue가 없는 같은 venueCode는 한 행만 생성한다. */
export const selectVenueConflictsToApply = (
  items: VenueConflictApplyItem[],
): VenueConflictApplyItem[] => {
  const seenCodes = new Set<string>();
  return items.filter((item) => {
    if (item.venueId != null) return true;
    const code = item.venueCode.trim().toUpperCase();
    if (seenCodes.has(code)) return false;
    seenCodes.add(code);
    return true;
  });
};

export const buildResolveVenueConflictsReq = (
  items: Array<{
    venueId: number;
    venueCode: string;
    venue: UpdateVenueReq;
  }>,
): ResolveVenueConflictsReq => ({
  resolutions: items.map(({ venueId, venueCode, venue }) => ({
    venueId,
    venueCode,
    venue,
  })),
});
